import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Compass, Home, Mail, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { ROUTE_PATTERNS } from "@/utils/route-registry";
import { deepSearch, type DeepSearchResult } from "@/utils/search-api";

/**
 * Curated quick-links shown when no better suggestion is available.
 * Kept short so the page stays scannable.
 */
const QUICK_LINKS: Array<{ to: string; label: string; description: string }> = [
  { to: "/", label: "Home", description: "Y.O. Ranch HQ overview" },
  { to: "/explore", label: "Explore", description: "Stories, exhibits, archive" },
  { to: "/on-display", label: "On Display", description: "Current exhibits" },
  { to: "/document-search", label: "Document Search", description: "Search the full archive" },
  { to: "/historical-figures", label: "Historical Figures", description: "Index of people" },
  { to: "/visit", label: "Plan a Visit", description: "Appointment-only access" },
];

/** Tokenise a path into search terms (drop ids/uuids/numbers). */
function pathToQuery(path: string): string {
  const tokens = path
    .split(/[\/\-_?&=#.]+/)
    .map((t) => decodeURIComponent(t).trim().toLowerCase())
    .filter((t) => t.length > 2 && !/^\d+$/.test(t) && !/^[0-9a-f]{8,}$/.test(t));
  return Array.from(new Set(tokens)).slice(0, 6).join(" ");
}

/** Cheap edit-distance for short strings — used to suggest the closest route. */
function distance(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  const prev = new Array(n + 1).fill(0).map((_, i) => i);
  const curr = new Array(n + 1).fill(0);
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j];
  }
  return prev[n];
}

/** Find the static route closest to the failed path. */
function suggestRoute(path: string): string | null {
  // Only compare against fully-static patterns — dynamic ones (with :param)
  // would mislead users.
  const candidates = ROUTE_PATTERNS
    .map((r) => r.pattern)
    .filter((p) => !p.includes(":") && p !== "/" && p.length > 1);

  let best: { route: string; score: number } | null = null;
  const target = path.toLowerCase();
  for (const route of candidates) {
    const d = distance(target, route.toLowerCase());
    // Allow up to 35% of the path length as edit distance.
    const threshold = Math.max(3, Math.floor(target.length * 0.35));
    if (d <= threshold && (!best || d < best.score)) best = { route, score: d };
  }
  return best?.route ?? null;
}

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const failedPath = location.pathname + location.search;

  // Avoid double-logging on Strict-Mode double-mount or hot reload.
  const loggedRef = useRef<string | null>(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<DeepSearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);

  const suggestion = useMemo(() => suggestRoute(location.pathname), [location.pathname]);
  const autoQuery = useMemo(() => pathToQuery(location.pathname), [location.pathname]);

  // 1) Log the 404 (fire-and-forget) and warn in console.
  useEffect(() => {
    if (loggedRef.current === failedPath) return;
    loggedRef.current = failedPath;

    console.warn("[404]", failedPath);
    void supabase
      .from("not_found_hits")
      .insert({
        path: failedPath.slice(0, 2048),
        referrer: (typeof document !== "undefined" && document.referrer) ? document.referrer.slice(0, 2048) : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 1024) : null,
      })
      .then(({ error }) => {
        if (error) console.warn("[404] log failed:", error.message);
      });
  }, [failedPath]);

  // 2) Auto-run a guided search using terms from the failed slug.
  useEffect(() => {
    if (!autoQuery) return;
    let cancelled = false;
    setSearching(true);
    deepSearch(autoQuery, null, 6)
      .then((res) => { if (!cancelled) setResults(res); })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [autoQuery]);

  const handleManualSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    navigate(`/document-search?q=${encodeURIComponent(q)}`);
  };

  return (
    <>
      <Helmet>
        <title>Page not found · Y.O. Ranch HQ</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      <section className="py-16 lg:py-24 bg-secondary/20 border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <p className="text-xs tracking-[0.3em] uppercase text-brass mb-3">Error 404</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-medium text-foreground mb-4">
            We couldn't find that page.
          </h1>
          <p className="text-muted-foreground leading-relaxed mb-2">
            The path{" "}
            <code className="text-xs bg-muted text-foreground px-1.5 py-0.5 rounded break-all">
              {failedPath}
            </code>{" "}
            doesn't match anything in our archive. It may have moved, been renamed, or never existed.
          </p>
          {suggestion && (
            <p className="mt-4 text-sm">
              <span className="text-muted-foreground">Did you mean</span>{" "}
              <Link
                to={suggestion}
                className="font-medium text-brass hover:underline inline-flex items-center gap-1"
              >
                <code className="text-sm">{suggestion}</code>
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              ?
            </p>
          )}
        </div>
      </section>

      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl space-y-10">
          {/* Manual search */}
          <div>
            <h2 className="font-serif text-xl font-medium mb-3 flex items-center gap-2">
              <Search className="h-4 w-4 text-brass" />
              Search the archive
            </h2>
            <form onSubmit={handleManualSearch} className="flex gap-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search people, exhibits, documents…"
                aria-label="Search"
                autoFocus
              />
              <Button type="submit" disabled={!query.trim()}>
                Search
              </Button>
            </form>
          </div>

          {/* Auto-derived results */}
          {autoQuery && (
            <div>
              <h2 className="font-serif text-xl font-medium mb-1 flex items-center gap-2">
                <Compass className="h-4 w-4 text-brass" />
                Possible matches
              </h2>
              <p className="text-xs text-muted-foreground mb-4">
                Based on terms from the requested URL: <em>{autoQuery}</em>
              </p>
              {searching ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-16 rounded-md bg-muted animate-pulse" />
                  ))}
                </div>
              ) : results && results.length > 0 ? (
                <div className="space-y-2">
                  {results.map((r) => (
                    <Card key={r.id} className="hover:border-brass/40 transition-colors">
                      <CardContent className="p-4">
                        <Link
                          to={r.source_url ?? `/document-search?q=${encodeURIComponent(r.source_title)}`}
                          className="block group"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-foreground group-hover:text-brass transition-colors">
                                {r.source_title}
                              </p>
                              {r.headline && (
                                <p
                                  className="text-sm text-muted-foreground mt-1 line-clamp-2"
                                  dangerouslySetInnerHTML={{ __html: r.headline }}
                                />
                              )}
                            </div>
                            {r.category && (
                              <Badge variant="outline" className="flex-shrink-0">
                                {r.category}
                              </Badge>
                            )}
                          </div>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No archive matches — try the search box above or browse the destinations below.
                </p>
              )}
            </div>
          )}

          {/* Quick destinations */}
          <div>
            <h2 className="font-serif text-xl font-medium mb-4 flex items-center gap-2">
              <Home className="h-4 w-4 text-brass" />
              Popular destinations
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {QUICK_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="block p-3 rounded-md border border-border bg-background hover:bg-secondary/50 hover:border-brass/40 transition-colors"
                >
                  <p className="font-medium text-foreground">{link.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{link.description}</p>
                </Link>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-border text-sm text-muted-foreground">
            Still can't find what you're looking for?{" "}
            <Link to="/contact" className="text-brass hover:underline inline-flex items-center gap-1">
              <Mail className="h-3.5 w-3.5" />
              Contact us
            </Link>
            .
          </div>
        </div>
      </section>
    </>
  );
};

export default NotFound;
